import React from "react";
import { motion } from "framer-motion";
import LeadForm from "../components/LeadForm";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, WHATSAPP_LINK } from "../lib/constants";
import useDocumentMeta from "../lib/useDocumentMeta";

const cards = [
    {
        id: "phone",
        icon: Phone,
        label: "Call us",
        value: PHONE_DISPLAY,
        href: `tel:${PHONE_TEL}`,
    },
    {
        id: "whatsapp",
        icon: MessageCircle,
        label: "WhatsApp",
        value: "Chat with us instantly",
        href: WHATSAPP_LINK,
        external: true,
    },
    {
        id: "email",
        icon: Mail,
        label: "Email",
        value: EMAIL,
        href: `mailto:${EMAIL}`,
    },
    {
        id: "address",
        icon: MapPin,
        label: "Studio",
        value: "Nagpur, Maharashtra · India",
    },
];

const ContactPage = () => {
    useDocumentMeta({
        title: "Contact Raj Graphics — Branding & Printing Studio in Nagpur",
        description: "Talk to Raj Graphics in Nagpur. Call, WhatsApp, email or request a quote for design, branding, printing and signage.",
        canonical: "/contact",
    });

    return (
        <main data-testid="contact-page" className="bg-[#050505]">
            {/* Hero */}
            <section className="relative pt-36 pb-16 md:pt-44 md:pb-24 overflow-hidden">
                <div className="absolute inset-0 grid-pattern radial-fade opacity-60 pointer-events-none" />
                <div className="absolute -top-32 right-0 w-[500px] h-[500px] rounded-full bg-gold/[0.07] blur-[120px] pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                    <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-5">
                        Get In Touch
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="font-poppins font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] max-w-3xl"
                        data-testid="contact-headline"
                    >
                        Let's <span className="gold-gradient-text">build your brand</span> together.
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="mt-6 font-inter text-base sm:text-lg text-zinc-400 max-w-2xl leading-relaxed"
                    >
                        Tell us about your project. We respond within a few business hours.
                    </motion.p>
                </div>
            </section>

            {/* Contact cards */}
            <section className="relative py-12 md:py-16 border-t border-white/5">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
                    {cards.map((c) => {
                        const Icon = c.icon;
                        const Comp = c.href ? "a" : "div";
                        const aProps = c.href
                            ? {
                                  href: c.href,
                                  ...(c.external
                                      ? { target: "_blank", rel: "noopener noreferrer" }
                                      : {}),
                              }
                            : {};
                        return (
                            <Comp
                                key={c.id}
                                {...aProps}
                                data-testid={`contact-card-${c.id}`}
                                className="group bg-white/[0.02] border border-white/5 rounded-2xl p-6 hover:border-gold/40 hover:bg-white/[0.04] transition-all"
                            >
                                <div className="w-11 h-11 rounded-lg bg-gold/10 border border-gold/20 flex items-center justify-center mb-5">
                                    <Icon size={18} className="text-gold" />
                                </div>
                                <div className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">
                                    {c.label}
                                </div>
                                <div className="font-poppins font-medium text-white text-base group-hover:text-gold transition-colors">
                                    {c.value}
                                </div>
                            </Comp>
                        );
                    })}
                </div>
            </section>

            <LeadForm />

            {/* Google Map */}
            <section
                className="relative py-16 md:py-24 border-t border-white/5 bg-[#080808]"
                data-testid="contact-map-section"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="max-w-2xl mb-10">
                        <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-4">
                            Find us
                        </div>
                        <h2 className="font-poppins font-semibold tracking-tight text-white text-3xl sm:text-4xl">
                            Visit the studio.
                        </h2>
                        <p className="mt-4 font-inter text-base text-zinc-400 leading-relaxed">
                            Drop by for a chat — or use the map below to plan your route.
                        </p>
                    </div>
                    <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#0c0c0c]">
                        <iframe
                            title="Raj Graphics — Studio Location"
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3720.8057343386777!2d79.119832!3d21.160128!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bd4c72feba1c5e1%3A0x6ee5345b76467318!2sRaj%20graphics!5e0!3m2!1sen!2sin!4v1777499544491!5m2!1sen!2sin"
                            width="100%"
                            height="450"
                            style={{ border: 0, display: "block", filter: "grayscale(0.2) contrast(1.05)" }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            data-testid="contact-map-iframe"
                        />
                    </div>
                </div>
            </section>

            {/* Hours */}
            <section className="relative py-16 md:py-20 border-t border-white/5 bg-[#080808]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <div className="font-inter text-xs uppercase tracking-[0.2em] text-gold mb-3">Mon — Sat</div>
                        <div className="font-poppins text-white text-2xl">10:00 — 19:00</div>
                    </div>
                    <div>
                        <div className="font-inter text-xs uppercase tracking-[0.2em] text-gold mb-3">Sunday</div>
                        <div className="font-poppins text-white text-2xl">By appointment</div>
                    </div>
                    <div>
                        <div className="font-inter text-xs uppercase tracking-[0.2em] text-gold mb-3">Response time</div>
                        <div className="font-poppins text-white text-2xl">Under 4 hours</div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default ContactPage;
